import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUserProfile } from './useUserProfile';
import { round3 } from '../domain/time';

/**
 * Excerpt Studio state — ONE in/out range on a media document that becomes
 * its own "(Excerpt …)" document, transcribed in full.
 *
 * Nothing here is persisted until "Create excerpt" submits the
 * `excerpt_media` job (ExcerptCreateModal). The range is local draft state:
 * set by dragging the strip, by ✂ on transcript lines, by typing times, or
 * by snapping IN/OUT to the playhead. "Play excerpt" loops playback inside
 * the range so the lawyer hears exactly what the cut will contain.
 *
 * Feature-gated by /auth/me `features.media_excerpts` (off → `enabled`
 * false and the studio renders nothing). Bounds and the Cloud Speech rate
 * come from the same features block so the cost line matches the server.
 * See plans/MEDIA_EXCERPT_PLAN.md.
 */
export default function useExcerpt({ mediaRef }) {
  const { profile } = useUserProfile();
  const features = profile?.features || {};
  const enabled = !!features.media_excerpts;
  const maxSeconds = Number(features.media_excerpt_max_seconds) || 3600;
  const minSeconds = Number(features.media_excerpt_min_seconds) || 1;
  const costPerMinute = Number(features.media_excerpt_cost_per_minute) || 0.024;

  const [mode, setMode] = useState(false);
  const [range, setRange] = useState(null);         // {start, end} seconds, or null
  const [cutMode, setCutMode] = useState('exact');  // 'exact' | 'fast' (video only)
  const [loop, setLoop] = useState(false);          // "Play excerpt" — loop inside the range
  const [duration, setDuration] = useState(0);
  const [lastMark, setLastMark] = useState(null);   // {start, end, via} for the "✓ marked" note
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const loopRef = useRef(loop);
  loopRef.current = loop;

  // ── Track media duration for the strip / clamping ───────────────────
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !enabled) return;
    const apply = () => { if (Number.isFinite(el.duration)) setDuration(el.duration); };
    apply();
    el.addEventListener('loadedmetadata', apply);
    el.addEventListener('durationchange', apply);
    return () => {
      el.removeEventListener('loadedmetadata', apply);
      el.removeEventListener('durationchange', apply);
    };
  }, [mediaRef, enabled, mode]);

  // ── Loop preview: past OUT ⇒ back to IN ─────────────────────────────
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !enabled || !mode) return;
    const onTime = () => {
      const r = rangeRef.current;
      if (!loopRef.current || !r) return;
      if (el.currentTime >= r.end - 0.05 || el.currentTime < r.start - 0.5) {
        el.currentTime = r.start;
      }
    };
    el.addEventListener('timeupdate', onTime);
    return () => el.removeEventListener('timeupdate', onTime);
  }, [mediaRef, enabled, mode]);

  const clamp = useCallback((t) => {
    const d = duration || Infinity;
    return Math.max(0, Math.min(Number(t) || 0, d));
  }, [duration]);

  const setRangeChecked = useCallback((start, end, via = 'edit') => {
    const s = round3(clamp(Math.min(start, end)));
    const e = round3(clamp(Math.max(start, end)));
    if (e - s < 0.05) return null;
    const next = { start: s, end: e };
    setRange(next);
    setLastMark({ ...next, via });
    return next;
  }, [clamp]);

  /** Typed/nudged IN: keeps at least 0.05 s below OUT (or seeds OUT 10 s later). */
  const setStart = useCallback((t) => {
    const cur = rangeRef.current;
    if (!cur) return setRangeChecked(t, clamp(t) + 10, 'in');
    return setRangeChecked(Math.min(clamp(t), cur.end - 0.05), cur.end, 'in');
  }, [clamp, setRangeChecked]);

  /** Typed/nudged OUT: keeps at least 0.05 s above IN (or seeds IN 10 s earlier). */
  const setEnd = useCallback((t) => {
    const cur = rangeRef.current;
    if (!cur) return setRangeChecked(Math.max(0, clamp(t) - 10), t, 'out');
    return setRangeChecked(cur.start, Math.max(clamp(t), cur.start + 0.05), 'out');
  }, [clamp, setRangeChecked]);

  /** ✂ on a transcript line: first line seeds the range, later lines extend it. */
  const markFromTranscript = useCallback((start, end) => {
    const cur = rangeRef.current;
    if (!cur) return setRangeChecked(start, end, 'transcript');
    return setRangeChecked(Math.min(cur.start, start), Math.max(cur.end, end), 'transcript');
  }, [setRangeChecked]);

  const clear = useCallback(() => { setRange(null); setLastMark(null); setLoop(false); }, []);

  const lengthSeconds = range ? round3(range.end - range.start) : 0;
  const estimatedCost = useMemo(
    () => (range ? (lengthSeconds / 60) * costPerMinute + 0.01 : 0),
    [range, lengthSeconds, costPerMinute],
  );
  const tooShort = !!range && lengthSeconds < minSeconds;
  const tooLong = !!range && lengthSeconds > maxSeconds;
  const valid = !!range && !tooShort && !tooLong;

  const mediaEl = useCallback(() => mediaRef.current, [mediaRef]);

  return {
    enabled, mode, setMode,
    range, setRange: setRangeChecked, setStart, setEnd, markFromTranscript, clear,
    cutMode, setCutMode, loop, setLoop,
    duration, lastMark, mediaEl,
    lengthSeconds, estimatedCost, valid, tooShort, tooLong, minSeconds, maxSeconds,
  };
}
